import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * BrandBanner3D
 * ---------------------------------------------------------------
 * 3D interactive brand banner for the DTS home. Keeps the original
 * brand image untouched (no distortion of logo/text/colors) and
 * layers depth + motion on top of it:
 *
 *  - Intro (~3s): soft light sweep + fade-in + depth settle
 *  - Ambient loop: slow floating, gentle glow pulse, rising particles
 *  - Desktop: mouse-follow 3D tilt (perspective) + parallax between
 *    background glow / particles / image / overlay text
 *  - Touch: click/tap triggers a one-shot light ripple
 *  - A11y: respects prefers-reduced-motion (static fallback)
 *  - Perf: pauses when offscreen via IntersectionObserver
 *
 * All animation is pure CSS keyframes + CSS custom props (--tx/--ty)
 * driven from a single pointermove handler. No render-on-move thrash,
 * no requestAnimationFrame loop.
 */

const BRAND_IMAGE_URL =
  'https://customer-assets-lqy194kg.emergentagent.net/job_tech-academy-hub-12/artifacts/81602d43742cef10_image.png';

// Small deterministic particle grid (no Math.random on render so SSR
// safe + no layout jump between first paint and hydration).
const PARTICLES = Array.from({ length: 22 }, (_, i) => {
  const seed = (i * 9301 + 49297) % 233280 / 233280;
  const seed2 = ((i + 1) * 7919) % 1000 / 1000;
  return {
    left: `${seed * 100}%`,
    top: `${seed2 * 100}%`,
    delay: `${(seed * 6).toFixed(2)}s`,
    duration: `${(6 + seed2 * 6).toFixed(2)}s`,
    size: 2 + Math.round(seed2 * 4),
  };
});

export default function BrandBanner3D() {
  const stageRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const [inView, setInView] = useState(true);
  const [rippleKey, setRippleKey] = useState(0);
  const [ripple, setRipple] = useState(null);

  // --- a11y: honor OS "reduce motion" preference ---------------------
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReducedMotion(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // --- perf: pause ambient animations when scrolled offscreen --------
  useEffect(() => {
    const el = stageRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => setInView(e.isIntersecting)),
      { threshold: 0.05 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  // --- desktop: mouse-follow 3D tilt (via CSS vars) ------------------
  const handlePointerMove = useCallback((e) => {
    if (reducedMotion) return;
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const cx = e.clientX - r.left;
    const cy = e.clientY - r.top;
    const nx = (cx / r.width) * 2 - 1;   // -1 .. 1
    const ny = (cy / r.height) * 2 - 1;  // -1 .. 1
    // soft clamp to avoid aggressive tilt near corners
    el.style.setProperty('--tx', Math.max(-1, Math.min(1, ny)).toFixed(3));
    el.style.setProperty('--ty', Math.max(-1, Math.min(1, -nx)).toFixed(3));
    el.style.setProperty('--mx', `${((cx / r.width) * 100).toFixed(2)}%`);
    el.style.setProperty('--my', `${((cy / r.height) * 100).toFixed(2)}%`);
  }, [reducedMotion]);

  const handlePointerLeave = useCallback(() => {
    const el = stageRef.current;
    if (!el) return;
    el.style.setProperty('--tx', '0');
    el.style.setProperty('--ty', '0');
  }, []);

  // --- click / tap: emit a one-shot light ripple ---------------------
  const handlePointerDown = useCallback((e) => {
    if (reducedMotion) return;
    const el = stageRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRipple({ x: e.clientX - r.left, y: e.clientY - r.top });
    setRippleKey((k) => k + 1);
  }, [reducedMotion]);

  const particleEls = useMemo(() => PARTICLES.map((p, i) => (
    <span
      key={i}
      className="brand3d-particle"
      style={{
        left: p.left, top: p.top,
        width: p.size, height: p.size,
        animationDelay: p.delay,
        animationDuration: p.duration,
      }}
      aria-hidden="true"
    />
  )), []);

  return (
    <section
      className={`brand3d-section ${inView ? 'is-active' : 'is-paused'} ${reducedMotion ? 'is-reduced' : ''}`}
      data-testid="home-brand-banner"
      aria-label="Digital Tech Solutions — La tua crescita digitale inizia qui"
    >
      <div
        ref={stageRef}
        className="brand3d-stage"
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onPointerDown={handlePointerDown}
      >
        {/* Depth plane 1: diffuse ambient glow (slowest parallax) */}
        <div className="brand3d-plane brand3d-plane--bg" aria-hidden="true">
          <span className="brand3d-glow brand3d-glow--a" />
          <span className="brand3d-glow brand3d-glow--b" />
        </div>

        {/* Depth plane 2: rising particles + digital connections */}
        <div className="brand3d-plane brand3d-plane--mid" aria-hidden="true">
          <svg className="brand3d-grid" viewBox="0 0 600 400" preserveAspectRatio="none" aria-hidden="true">
            <defs>
              <linearGradient id="brand3d-line" x1="0" x2="1" y1="0" y2="0">
                <stop offset="0" stopColor="#c6f24e" stopOpacity="0" />
                <stop offset=".5" stopColor="#c6f24e" stopOpacity=".55" />
                <stop offset="1" stopColor="#60a5fa" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d="M 20 320 L 160 220 L 300 260 L 440 180 L 580 230" stroke="url(#brand3d-line)" strokeWidth="1.2" fill="none" className="brand3d-line brand3d-line--1" />
            <path d="M 40 80 L 180 140 L 320 90 L 460 150 L 580 100" stroke="url(#brand3d-line)" strokeWidth="1" fill="none" className="brand3d-line brand3d-line--2" />
            <path d="M 80 360 L 220 300 L 360 340 L 500 290 L 600 320" stroke="url(#brand3d-line)" strokeWidth=".8" fill="none" className="brand3d-line brand3d-line--3" />
          </svg>
          <div className="brand3d-particles">{particleEls}</div>
        </div>

        {/* Depth plane 3: the original brand image — never distorted */}
        <div className="brand3d-plane brand3d-plane--front">
          <div className="brand3d-image-wrap">
            <img
              src={BRAND_IMAGE_URL}
              alt=""
              loading="lazy"
              decoding="async"
              draggable="false"
              className="brand3d-image"
              data-testid="home-brand-banner-img"
            />
            {/* intro light sweep — plays once */}
            <span className="brand3d-sweep" aria-hidden="true" />
            {/* hover spotlight follows pointer */}
            <span className="brand3d-spotlight" aria-hidden="true" />
            {/* click/touch ripple */}
            {ripple && !reducedMotion && (
              <span
                key={rippleKey}
                className="brand3d-ripple"
                style={{ left: ripple.x, top: ripple.y }}
                aria-hidden="true"
                onAnimationEnd={() => setRipple(null)}
              />
            )}
          </div>
        </div>
      </div>

      {/* HTML overlay — rendered BELOW the stage (not on top of the baked
          image text) so the brand copy stays crisp and never collides with
          the artwork. The headline is still part of the same section and
          inherits the stage's parallax via its own transform below. */}
      <div className="brand3d-caption" data-testid="brand3d-overlay">
        <p className="brand3d-eyebrow">DIAMO FORMA ALLE TUE IDEE</p>
        <h2 className="brand3d-headline">
          LA TUA CRESCITA DIGITALE{' '}
          <span className="brand3d-highlight">INIZIA QUI</span>
        </h2>
      </div>

      {/* All animation + 3D tilt rules scoped locally to avoid any
          collision with the global stylesheet. */}
      <style>{`
        .brand3d-section {
          position: relative;
          padding: 56px 20px;
          background: transparent;
          overflow: hidden;
        }
        @media (min-width: 768px) { .brand3d-section { padding: 80px 32px; } }

        .brand3d-stage {
          position: relative;
          max-width: 1100px;
          margin: 0 auto;
          /* Match the native 849x371 ratio of the brand image so nothing
             is cropped and no letterboxing is visible. */
          aspect-ratio: 849 / 371;
          border-radius: 28px;
          overflow: hidden;
          border: 1px solid rgba(198, 242, 78, 0.28);
          box-shadow: 0 30px 80px -20px rgba(0,0,0,.65);
          perspective: 1200px;
          transform-style: preserve-3d;
          cursor: crosshair;
          --tx: 0; --ty: 0; --mx: 50%; --my: 50%;
          background: #0b0f14;
        }

        .brand3d-plane {
          position: absolute; inset: 0;
          transform-style: preserve-3d;
          will-change: transform;
          transition: transform .35s cubic-bezier(.2,.9,.3,1);
          pointer-events: none;
        }
        .brand3d-plane--bg    { transform: translateZ(-80px) rotateX(calc(var(--tx) * 2deg)) rotateY(calc(var(--ty) * 2deg)); }
        .brand3d-plane--mid   { transform: translateZ(0)     rotateX(calc(var(--tx) * 4deg)) rotateY(calc(var(--ty) * 4deg)); }
        .brand3d-plane--front { transform: translateZ(60px)  rotateX(calc(var(--tx) * 6deg)) rotateY(calc(var(--ty) * 6deg)); }

        /* ===== Ambient glow (plane 1) ===== */
        .brand3d-glow { position: absolute; border-radius: 50%; filter: blur(70px); opacity: .55; }
        .brand3d-glow--a {
          width: 55%; aspect-ratio: 1;
          top: -15%; left: -10%;
          background: radial-gradient(circle, rgba(198,242,78,.55), transparent 60%);
          animation: brand3d-glow-a 11s ease-in-out infinite alternate;
        }
        .brand3d-glow--b {
          width: 55%; aspect-ratio: 1;
          bottom: -15%; right: -10%;
          background: radial-gradient(circle, rgba(96,165,250,.5), transparent 60%);
          animation: brand3d-glow-b 13s ease-in-out infinite alternate;
        }
        @keyframes brand3d-glow-a {
          0%   { transform: translate(0,0) scale(1); opacity: .5; }
          100% { transform: translate(4%, 3%) scale(1.08); opacity: .7; }
        }
        @keyframes brand3d-glow-b {
          0%   { transform: translate(0,0) scale(1); opacity: .4; }
          100% { transform: translate(-5%, -2%) scale(1.1); opacity: .65; }
        }

        /* ===== Digital connections (plane 2) ===== */
        .brand3d-grid { position: absolute; inset: 0; width: 100%; height: 100%; opacity: .55; }
        .brand3d-line {
          stroke-dasharray: 300;
          stroke-dashoffset: 300;
          animation: brand3d-draw 2.4s cubic-bezier(.2,.9,.3,1) forwards;
        }
        .brand3d-line--2 { animation-delay: .35s; }
        .brand3d-line--3 { animation-delay: .65s; }
        @keyframes brand3d-draw { to { stroke-dashoffset: 0; } }

        /* ===== Rising particles (plane 2) ===== */
        .brand3d-particles { position: absolute; inset: 0; }
        .brand3d-particle {
          position: absolute;
          border-radius: 50%;
          background: radial-gradient(circle, #c6f24e 0%, rgba(198,242,78,.15) 60%, transparent 70%);
          opacity: 0;
          animation: brand3d-rise ease-in-out infinite;
          filter: drop-shadow(0 0 4px rgba(198,242,78,.55));
        }
        @keyframes brand3d-rise {
          0%   { opacity: 0; transform: translateY(10px) scale(.6); }
          20%  { opacity: 1; }
          80%  { opacity: 1; }
          100% { opacity: 0; transform: translateY(-50px) scale(1.1); }
        }

        /* ===== Main brand image (plane 3) ===== */
        .brand3d-image-wrap {
          position: absolute; inset: 0;
          display: flex; align-items: center; justify-content: center;
          overflow: hidden;
          border-radius: 28px;
        }
        .brand3d-image {
          max-width: 100%; max-height: 100%;
          width: 100%; height: 100%;
          object-fit: contain; object-position: center;
          user-select: none; -webkit-user-drag: none;
          opacity: 0;
          transform: scale(.98);
          animation: brand3d-img-in 1.6s cubic-bezier(.2,.9,.3,1) .3s forwards,
                     brand3d-img-float 9s ease-in-out 2s infinite;
        }
        @keyframes brand3d-img-in {
          0%   { opacity: 0; transform: scale(.95) translateZ(0); filter: brightness(.7) saturate(.85); }
          100% { opacity: 1; transform: scale(1)   translateZ(0); filter: brightness(1) saturate(1); }
        }
        @keyframes brand3d-img-float {
          0%, 100% { transform: translateY(0) scale(1); }
          50%      { transform: translateY(-6px) scale(1.005); }
        }

        /* ===== Intro light sweep ===== */
        .brand3d-sweep {
          position: absolute; inset: 0;
          background: linear-gradient(115deg,
            transparent 30%,
            rgba(255,255,255,.22) 48%,
            rgba(198,242,78,.35) 50%,
            rgba(255,255,255,.22) 52%,
            transparent 70%);
          transform: translateX(-120%);
          animation: brand3d-sweep 2.6s cubic-bezier(.65,0,.35,1) .35s 1 forwards;
          mix-blend-mode: screen;
          pointer-events: none;
        }
        @keyframes brand3d-sweep {
          0%   { transform: translateX(-120%); opacity: 1; }
          85%  { opacity: 1; }
          100% { transform: translateX(120%);  opacity: 0; }
        }

        /* ===== Hover spotlight (follows cursor) ===== */
        .brand3d-spotlight {
          position: absolute; inset: 0;
          pointer-events: none;
          opacity: 0;
          transition: opacity .35s ease;
          background: radial-gradient(circle at var(--mx) var(--my),
            rgba(198,242,78,.22), transparent 25%);
          mix-blend-mode: screen;
        }
        .brand3d-stage:hover .brand3d-spotlight { opacity: 1; }

        /* ===== Click / tap ripple ===== */
        .brand3d-ripple {
          position: absolute;
          width: 8px; height: 8px;
          border-radius: 50%;
          background: rgba(198,242,78,.6);
          box-shadow: 0 0 24px 4px rgba(198,242,78,.55);
          transform: translate(-50%, -50%) scale(1);
          animation: brand3d-ripple .9s cubic-bezier(.2,.6,.2,1) forwards;
          pointer-events: none;
          mix-blend-mode: screen;
        }
        @keyframes brand3d-ripple {
          0%   { opacity: .9; transform: translate(-50%, -50%) scale(1); }
          100% { opacity: 0; transform: translate(-50%, -50%) scale(45); }
        }

        /* ===== Caption BELOW the stage (crisp HTML, no image overlap) ===== */
        .brand3d-caption {
          position: relative;
          max-width: 1100px;
          margin: 24px auto 0;
          padding: 0 8px;
          text-align: center;
          display: flex; flex-direction: column; align-items: center; gap: 10px;
        }
        @media (min-width: 768px) { .brand3d-caption { margin-top: 32px; gap: 14px; } }
        .brand3d-eyebrow {
          font-family: ui-monospace, 'JetBrains Mono', 'Fira Code', monospace;
          font-size: 11px;
          letter-spacing: .28em;
          color: rgba(255,255,255,.6);
          margin: 0;
          text-transform: uppercase;
        }
        @media (min-width: 768px) { .brand3d-eyebrow { font-size: 13px; } }
        .brand3d-headline {
          margin: 0;
          font-weight: 900;
          font-size: clamp(22px, 3.8vw, 40px);
          line-height: 1.08;
          letter-spacing: -.01em;
          color: #fff;
          text-transform: uppercase;
        }
        .brand3d-highlight {
          display: inline-block;
          background: linear-gradient(90deg, #c6f24e 0%, #fde047 55%, #c6f24e 100%);
          background-size: 200% 100%;
          -webkit-background-clip: text;
          background-clip: text;
          color: transparent;
          animation: brand3d-highlight 5.5s ease-in-out infinite;
          filter: drop-shadow(0 0 18px rgba(198,242,78,.35));
        }
        @keyframes brand3d-highlight {
          0%, 100% { background-position: 0% 50%; }
          50%      { background-position: 100% 50%; }
        }

        /* ===== Pause when offscreen ===== */
        .brand3d-section.is-paused .brand3d-glow,
        .brand3d-section.is-paused .brand3d-particle,
        .brand3d-section.is-paused .brand3d-image,
        .brand3d-section.is-paused .brand3d-highlight {
          animation-play-state: paused;
        }

        /* ===== Reduced motion: static, respectful fallback ===== */
        .brand3d-section.is-reduced .brand3d-stage { perspective: none; cursor: default; }
        .brand3d-section.is-reduced .brand3d-plane { transform: none !important; transition: none; }
        .brand3d-section.is-reduced .brand3d-image { animation: none; opacity: 1; transform: none; filter: none; }
        .brand3d-section.is-reduced .brand3d-glow,
        .brand3d-section.is-reduced .brand3d-particle,
        .brand3d-section.is-reduced .brand3d-highlight,
        .brand3d-section.is-reduced .brand3d-line { animation: none; }
        .brand3d-section.is-reduced .brand3d-sweep { display: none; }
      `}</style>
    </section>
  );
}
